import { useCallback, useMemo, useState } from "react";

import contactService from "../services/contactService";
import {
  parseCsvFile,
  parseDelimitedText,
  suggestMapping,
} from "./useImportContact";

function readSetup() {
  try {
    const parsed = JSON.parse(localStorage.getItem("whatsapp_setup") || "{}");
    return {
      workspaceId: parsed.workspaceId || parsed.workspace_id || null,
      whatsappAccountId:
        parsed.whatsappAccountId ||
        parsed.whatsapp_account_id ||
        parsed.accountId ||
        parsed.account_id ||
        null,
      phoneNumberId: parsed.phoneNumberId || parsed.phone_number_id || null,
    };
  } catch {
    return { workspaceId: null, whatsappAccountId: null, phoneNumberId: null };
  }
}

const EMPTY = {
  columns: [],
  rows: [],
  totalRows: 0,
  truncated: false,
};

const readError = (err, fallback) =>
  err?.response?.data?.message ||
  Object.values(err?.response?.data?.errors || {})[0]?.[0] ||
  err?.message ||
  fallback;

/**
 * State machine for the Import Contacts wizard:
 *   source  ->  map  ->  result
 */
export default function useContactImport() {
  const ids = useMemo(readSetup, []);
  const ready = Boolean(
    ids.workspaceId && ids.whatsappAccountId && ids.phoneNumberId,
  );

  const [step, setStep] = useState("source");
  const [loading, setLoading] = useState(false); // parsing / fetching a source
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");

  const [data, setData] = useState(EMPTY);
  const [sourceLabel, setSourceLabel] = useState("");
  const [mapping, setMappingState] = useState({});
  const [metadataColumns, setMetadataColumns] = useState([]);
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setStep("source");
    setLoading(false);
    setImporting(false);
    setError("");
    setData(EMPTY);
    setSourceLabel("");
    setMappingState({});
    setMetadataColumns([]);
    setResult(null);
  }, []);

  const acceptParsed = useCallback((parsed, label, suggested) => {
    const guess = suggested || suggestMapping(parsed.columns);
    const mapped = new Set(Object.values(guess).filter(Boolean));

    setData({
      columns: parsed.columns,
      rows: parsed.rows,
      totalRows: parsed.totalRows ?? parsed.rows.length,
      truncated: Boolean(parsed.truncated),
    });
    setMappingState(guess);
    // by default every unmapped column is kept as metadata
    setMetadataColumns(parsed.columns.filter((c) => !mapped.has(c)));
    setSourceLabel(label);
    setError("");
    setStep("map");
  }, []);

  const loadFromFile = useCallback(
    async (file) => {
      setError("");
      setLoading(true);
      try {
        const parsed = await parseCsvFile(file);
        acceptParsed(parsed, file.name);
      } catch (err) {
        setError(readError(err, "Couldn't read that file."));
      } finally {
        setLoading(false);
      }
    },
    [acceptParsed],
  );

  const loadFromPaste = useCallback(
    (text) => {
      setError("");
      try {
        const parsed = parseDelimitedText(text);
        acceptParsed(parsed, "Pasted data");
      } catch (err) {
        setError(readError(err, "Couldn't read the pasted data."));
      }
    },
    [acceptParsed],
  );

  const loadFromGoogleSheet = useCallback(
    async (url) => {
      if (!ready) {
        setError("Connect a WhatsApp number first.");
        return;
      }
      setError("");
      setLoading(true);
      try {
        const res = await contactService.fetchGoogleSheet(
          ids.workspaceId,
          ids.whatsappAccountId,
          ids.phoneNumberId,
          url.trim(),
        );
        const payload = res?.data?.data || {};
        acceptParsed(
          {
            columns: payload.columns || [],
            rows: payload.rows || [],
            totalRows: payload.total_rows ?? (payload.rows || []).length,
            truncated: Boolean(payload.truncated),
          },
          "Google Sheet",
          payload.suggested_mapping,
        );
      } catch (err) {
        setError(readError(err, "Couldn't load that Google Sheet."));
      } finally {
        setLoading(false);
      }
    },
    [ready, ids, acceptParsed],
  );

  const setFieldMapping = useCallback((field, column) => {
    setMappingState((prev) => {
      const next = { ...prev };
      // a column can only feed one field
      Object.keys(next).forEach((f) => {
        if (next[f] === column) delete next[f];
      });
      if (column) next[field] = column;
      else delete next[field];
      return next;
    });
    // a column mapped to a field is no longer "extra"
    if (column) {
      setMetadataColumns((prev) => prev.filter((c) => c !== column));
    }
  }, []);

  const toggleMetadataColumn = useCallback((column) => {
    setMetadataColumns((prev) =>
      prev.includes(column)
        ? prev.filter((c) => c !== column)
        : [...prev, column],
    );
    // moving a column to metadata clears any field it was mapped to
    setMappingState((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((f) => {
        if (next[f] === column) delete next[f];
      });
      return next;
    });
  }, []);

  const runImport = useCallback(async () => {
    if (!ready) {
      setError("Connect a WhatsApp number first.");
      return;
    }
    if (!mapping.phone_number) {
      setError("Map a column to Phone number to continue.");
      return;
    }
    setError("");
    setImporting(true);
    try {
      const res = await contactService.import(
        ids.workspaceId,
        ids.whatsappAccountId,
        ids.phoneNumberId,
        {
          mapping,
          metadata_columns: metadataColumns,
          rows: data.rows,
        },
      );
      setResult(res?.data?.data || null);
      setStep("result");
    } catch (err) {
      setError(readError(err, "Import failed. Please try again."));
    } finally {
      setImporting(false);
    }
  }, [ready, ids, mapping, metadataColumns, data.rows]);

  return {
    ready,
    step,
    setStep,
    loading,
    importing,
    error,
    setError,
    columns: data.columns,
    rows: data.rows,
    totalRows: data.totalRows,
    truncated: data.truncated,
    sourceLabel,
    mapping,
    metadataColumns,
    result,
    loadFromFile,
    loadFromPaste,
    loadFromGoogleSheet,
    setFieldMapping,
    toggleMetadataColumn,
    runImport,
    reset,
  };
}
